/**
 * AuthLayout Component
 *
 * Responsive authentication layout providing consistent branding and UX across all auth flows.
 * Creates a branded authentication experience with split-screen design on desktop and
 * full-screen scrollable layout on mobile.
 *
 * Key architectural decisions:
 * - Split 60/40 layout on desktop for visual hierarchy and brand presence
 * - Full-screen modal approach prevents interaction with underlying content
 * - Channel-specific branding loaded dynamically for multi-tenant support
 * - Fixed positioning with z-index ensures overlay above all content
 *
 * Business rules:
 * - Auth background image fallback to cover image if not specified
 * - Logo links back to homepage for escape route
 * - Support FAB hidden on onboarding to prevent mobile UX issues (DEV-1878)
 * - Close button provides explicit exit from auth flow
 *
 * UX considerations:
 * - Body scroll locked on desktop to prevent background interaction
 * - Mobile allows scrolling for long forms (registration, password reset)
 * - Centered variant for special flows (MFA setup, account deletion)
 * - Smooth transitions and hover states for interactive elements
 *
 * @see .claude/ui-component-rules.md - Responsive design patterns
 * @see AuthContext.jsx - Authentication state management
 */

import styled, { css } from "styled-components";

import Logo from "../../ui/Logo";
import { useChannel } from "../../hooks/useChannel";
import { useChatBot } from "../../hooks/useChatBot";

import IconXMark from "../../icons/IconXMark";
import IconSupportFab from "../../icons/IconSupportFab";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { useControlScroll } from "../../hooks/useControlScroll";
import { useEffect } from "react";

const StyledAuthLayout = styled.div`
  width: 100%;
  height: 100%;
  max-height: 100vh;
  background-color: var(--color-surface-primary);
  position: fixed;
  top: 0;
  left: 0;
  z-index: var(--z-index-auth-layout);
  overflow: hidden;

  @media screen and (max-width: 767px) {
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
  }
`;

const LeftSection = styled.div<{ $imageUrl?: string; $variant?: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 60%;
  height: 100%;
  background-color: var(--theme-action-primary);
  background-image: ${props => props.$imageUrl ? `url(${props.$imageUrl})` : 'none'};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;

  ${props => props.$variant === 'centered' && css`
    display: none;
  `}

  /* WHY: iPad portrait optimization (768px - 1023px)
     UX Decision: Hide image on iPad portrait mode to avoid cramped appearance
     Treats iPad portrait like mobile for better form readability */
  @media screen and (max-width: 1023px) {
    display: none;
  }
`;

const RightSection = styled.div<{ $variant?: string }>`
  position: absolute;
  top: 0;
  right: 0;
  width: 40%;
  height: 100%;
  background-color: var(--color-surface-primary);
  display: flex;
  align-items: center;
  justify-content: center;

  ${props => props.$variant === 'centered' && css`
    width: 100%;
    left: 0;
  `}

  /* WHY: iPad portrait optimization (768px - 1023px)
     UX Decision: Full width on iPad portrait since image is hidden
     Provides maximum space for form content */
  @media screen and (max-width: 1023px) {
    width: 100%;
    position: relative;
  }
`;

const Content = styled.div<{ $variant?: string; $mobileCompact?: boolean }>`
  width: 100%;
  max-width: 720px; /* DEV-1884: Increased from 610px to reduce paragraph text wrapping on desktop */
  max-height: 810px;
  padding: 64px 88px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  overflow-y: ${props => props.$variant === 'centered' ? 'visible' : 'auto'};
  box-sizing: border-box;

  /* WHY: Unified mobile and tablet optimization
     UX Decision: Consistent padding and layout for all devices under 1024px
     Provides better user experience on tablets and phones */
  @media screen and (max-width: 1023px) {
    position: relative;
    z-index: 1; /* WHY: Sit above MobileBackgroundImage (z-index: 0) */
    padding: ${props => props.$mobileCompact ? '20px 8px 100px' : '16px 8px'};
    max-height: none;
    min-height: 100vh;
    justify-content: ${props => props.$mobileCompact ? 'flex-start' : 'center'};
    gap: 16px;
    margin-top: 0;
    max-width: 100%;
  }
`;

// WHY: width: 100% ensures all auth forms render at the same width regardless of content length.
// Without this, flex align-items: center on the parent causes forms to shrink-to-fit their content,
// making forms with shorter text (e.g. password reset) visibly narrower than forms with longer text.
const FormContainer = styled.div`
  width: 100%;
`;

const SupportFAB = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: var(--theme-action-primary);
  border: none;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.25);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s, box-shadow 0.2s;
  z-index: 1000;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.35);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 24px;
    height: 24px;
  }

  @media screen and (max-width: 767px) {
    bottom: 16px;
    right: 16px;
  }
`;

const LogoContainer = styled.div<{ $variant?: string }>`
  position: absolute;
  top: 16px;
  left: 16px;
  width: 110px;
  height: 96px;
  z-index: 10;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 0.6;
  }

  @media screen and (max-width: 767px) {
    display: none;
  }
`;

const MobileLogoContainer = styled.div`
  cursor: pointer;
  transition: opacity 0.2s;

  &:active {
    opacity: 0.6;
  }
`;

const ContentHeader = styled.header`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 3;
`;

const CloseBox = styled.div`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  margin: 16px;
  cursor: pointer;
  color: var(--text-primary, rgba(255, 255, 255, 0.9));
`;

/**
 * Mobile background image layer
 *
 * WHY: Per Figma design (node 2325:8032), mobile signup shows background image
 * at top with gradient overlay fading to dark background below.
 *
 * DESIGN SPECS:
 * - Position: Fixed at top, centered horizontally
 * - Height: 263px (matches Figma specification)
 * - Gradient overlay: Semi-transparent dark layer + fade to solid background
 * - Z-index: 0 (behind content layer)
 */
const MobileBackgroundImage = styled.div<{ $imageUrl?: string }>`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 395px;
  height: 263px;
  background-image: ${props => props.$imageUrl ? `url(${props.$imageUrl})` : 'none'};
  background-size: cover;
  background-position: center;
  z-index: 0;

  /* Gradient overlay matching Figma specification */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(90deg, rgba(19, 20, 25, 0.7) 0%, rgba(19, 20, 25, 0.7) 100%),
      linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, var(--color-surface-primary, rgba(19, 20, 25, 1)) 100%);
  }

  @media screen and (min-width: 1024px) {
    display: none;
  }
`;

/**
 * Mobile form container with rounded corners and backdrop blur
 *
 * WHY: Per Figma design (node 2325:8032), form content sits in semi-transparent
 * rounded container with backdrop blur effect for visual depth.
 *
 * DESIGN SPECS:
 * - Background: rgba(19,20,25,0.7) - surface/scrim
 * - Border radius: 16px
 * - Backdrop blur: 2.7px
 * - Padding: 8px horizontal, 40px vertical
 * - Gap between children: 16px
 */
const MobileFormContainer = styled.div<{ $mobileCompact?: boolean }>`
  width: 100%;
  max-width: 100%;
  background-color: rgba(19, 20, 25, 0.7); /* surface/scrim from Figma */
  border-radius: 16px;
  /* DEV-1880: Compact mode (signup) uses tight padding; default (login) matches Figma centered design */
  padding: ${props => props.$mobileCompact ? '24px 16px' : '40px 8px'};
  backdrop-filter: blur(2.7px);
  -webkit-backdrop-filter: blur(2.7px); /* Safari support */
  display: flex;
  flex-direction: column;
  gap: ${props => props.$mobileCompact ? '12px' : '16px'};
  align-items: center;
  box-sizing: border-box;
  overflow-x: hidden;

  /* DEV-1880: Scale auth form headings to h2 visual size on mobile.
     Auth screens use h3/h4 for desktop sizing. MobileFormContainer
     only renders on mobile (<=767px), so this override safely promotes
     headings to match the Figma mobile designs without affecting desktop. */
  & h3, & h4 {
    font-family: var(--theme-font-family);
    font-size: var(--headings-h2-font-size);
    font-weight: var(--headings-h2-font-weight);
    line-height: var(--headings-h2-line-height);
    letter-spacing: var(--headings-h2-letter-spacing);
  }

  @media screen and (max-width: 480px) {
    & h3, & h4 {
      font-size: var(--headings-h2-mobile-font-size, var(--headings-h2-font-size));
      font-weight: var(--headings-h2-mobile-font-weight, var(--headings-h2-font-weight));
      line-height: var(--headings-h2-mobile-line-height, var(--headings-h2-line-height));
    }
  }

  @media screen and (min-width: 1024px) {
    display: none;
  }
`;

export interface AuthLayoutProps {
  children: React.ReactNode;
  goBack?: boolean;
  reduceLogo?: boolean;
  variant?: 'split' | 'centered';
  /** When true, mobile layout uses tight spacing and scrolls from top (for long forms like signup).
   *  When false (default), mobile layout centers content vertically (for short forms like login). */
  mobileCompact?: boolean;
  /** Optional close handler override. When provided, the X button calls this
   *  instead of the default navigate(-1) or navigate("/") behaviour.
   *  Useful for flows embedded in account pages where close should go
   *  to a specific route rather than browser history back. */
  onClose?: () => void;
}

/**
 * Authentication layout wrapper providing branded auth experience.
 *
 * WHY: Centralised layout ensures consistent UX across all auth flows
 * (login, signup, password reset, MFA) while maintaining brand identity.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Auth form content
 * @param {boolean} props.goBack - Navigate back vs home on close
 * @param {boolean} props.reduceLogo - Use smaller logo variant
 * @param {string} props.variant - 'split' or 'centered' layout
 * @returns {JSX.Element} Branded authentication layout
 */
function AuthLayout({ children, goBack, reduceLogo = false, variant = 'split', mobileCompact = false, onClose }: AuthLayoutProps): JSX.Element | null {
  const { channel, isLoading } = useChannel();
  const { hideLauncher, showLauncher } = useChatBot();
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ query: "(max-width: 767px)" });
  const controlScroll = useControlScroll();

  useEffect(() => {
    // WHY: Lock scroll on desktop to prevent background interaction
    // Mobile needs scroll for long forms (registration has many fields)
    if (!isMobile) {
      document.body.style.overflow = "hidden";
    }

    // WHY: Cleanup function restores scroll on unmount
    // Prevents scroll lock persisting after auth flow completes
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);

  useEffect(() => {
    // WHY: Disable app scroll controller during auth
    // Auth layout manages its own scroll behaviour
    controlScroll(false);
  }, []);

  // DEV-1878: Hide Intercom chat launcher on auth/onboarding screens
  // DEV-1955: FIX — Previously used hideChatBot/showChatBot which calls
  // Intercom('hide')/Intercom('show'). The 'show' command OPENS the messenger
  // panel, causing the chat to pop open every time a user exits an auth screen.
  // Now uses hideLauncher/showLauncher which toggles hide_default_launcher,
  // controlling only the floating button visibility without opening the messenger.
  useEffect(() => {
    hideLauncher();
    return () => {
      showLauncher();
    };
  }, [hideLauncher, showLauncher]);

  // WHY: Wait for channel data to avoid layout flash
  // Channel provides branding assets (logo, background, colors)
  if (isLoading) return null;

  const { logo_url, logo_url_mobile, auth_background_image } = channel;

  function handleClose(): void {
    // WHY: onClose callback takes priority for flows that need explicit routing
    // (e.g. account transfer overlay should close to /account/profile, not history back)
    if (onClose) {
      controlScroll(true);
      onClose();
      return;
    }
    // WHY: goBack prop determines close behaviour
    // Some flows need back navigation (multi-step), others go home
    if (goBack) navigate(-1);
    if (!goBack) {
      controlScroll(true); // Re-enable app scroll
      navigate("/");
    }
  }

  function handleLogoClick(): void {
    // WHY: Logo acts as escape hatch to homepage
    // Users expect logo to navigate home (standard UX pattern)
    controlScroll(true);
    navigate("/");
  }

  return (
    <StyledAuthLayout>
      {!isMobile && (
        <LogoContainer $variant={variant} onClick={handleLogoClick}>
          <Logo src={logo_url} size="large" />
        </LogoContainer>
      )}
      {!isMobile && (
        <LeftSection $imageUrl={auth_background_image || channel?.cover_image_url} $variant={variant}>
          {/* {variant !== 'centered' && (
            <Logo src={logo_url} size="superLarge" />
          )} */}
        </LeftSection>
      )}

      {!isMobile && (
        <RightSection $variant={variant}>
          <Content $variant={variant}>
            <FormContainer>{children}</FormContainer>
          </Content>
        </RightSection>
      )}

      {isMobile && (
        <>
          <MobileBackgroundImage $imageUrl={auth_background_image || channel?.cover_image_url} />
          <Content $variant={variant} $mobileCompact={mobileCompact}>
            <MobileFormContainer $mobileCompact={mobileCompact}>
              <MobileLogoContainer onClick={handleLogoClick}>
                <Logo
                  size={reduceLogo ? "small" : "small"}
                  src={logo_url_mobile || logo_url}
                />
              </MobileLogoContainer>
              <FormContainer>{children}</FormContainer>
            </MobileFormContainer>
          </Content>
        </>
      )}

      <ContentHeader>
        <CloseBox onClick={handleClose}>
          <IconXMark color="currentColor" />
        </CloseBox>
      </ContentHeader>

      {/* DEV-1878: Hide live chat FAB on onboarding modals */}
      {/* <SupportFAB onClick={showChatBot} aria-label="Open support chat">
        <IconSupportFab />
      </SupportFAB> */}
    </StyledAuthLayout>
  );
}

export default AuthLayout;
